import { inject, Injectable } from '@angular/core'
import { forkJoin, Observable, of } from 'rxjs'
import { Client } from '../../../models/client'
import { FeathersService } from '../../feathers.service'
import { concatMap, defaultIfEmpty, filter, first, map, shareReplay, switchMap } from 'rxjs/operators'
import { Assistance } from '../../../models/assistance'
import { AlertController, ModalController, NavController } from '@ionic/angular'
import { AssistanceStatusesService } from '../codelists/assistance-statuses.service'
import { AssistanceStatusName } from '../../../models/assistanceStatus'
import {
  SimpleAssistanceListComponent
} from '../../../features/assistance/simple-assistance-list/simple-assistance-list.component'

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private feathers = inject(FeathersService)
  private statusService = inject(AssistanceStatusesService)
  private modalCtrl = inject(ModalController)

  allClients$: Observable<Client[]>
  activeClients$: Observable<Client[]>

  public getAllWithCaching(): Observable<Client[]> {
    if (!this.allClients$) this.initAllClients()
    return this.allClients$
  }

  public getActiveWithCaching(): Observable<Client[]> {
    if (!this.activeClients$) this.initActiveClients()
    return this.activeClients$
  }

  public getById(clientId: number, allowCache = true): Observable<Client> {
    if (allowCache) {
      return this.getAllWithCaching().pipe(
        map((clients) => {
          const filtered = clients.filter((client) => client.id === clientId)
          if (filtered.length > 0) return filtered[0]
          return undefined
          // throw new Error(`Client ID not found ${id}`)
        }),
        switchMap((client) => {
          if (client) return of(client)
          return getUncachedResult(clientId, this.feathers)
        }),
      )
    }

    return getUncachedResult(clientId, this.feathers)

    function getUncachedResult(id: number, feathers: FeathersService) {
      return feathers.find(Client, { id, useDeprecatedHooks: true }).pipe(
        map((c) => c.find((client) => client.id === id)),
        // The live store can emit before the requested client has arrived (or after it's gone) — wait for an
        // emission that actually contains it instead of taking whatever came first and crashing on it.
        filter((c): c is Client => !!c),
        first(),
      )
    }
  }

  private initAllClients() {
    this.allClients$ = this.feathers.find(Client, { useDeprecatedHooks: true }).pipe(
      map((clients) => clients.sort(Client.sortByName)),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
  }

  private initActiveClients() {
    this.activeClients$ = this.getAllWithCaching().pipe(
      map((clients) => clients.filter((c) => !c.deactivatedAt)),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
  }

  save(client: Client): Promise<Client> {
    return this.feathers.upsertWithFactory(Client, client, { query: { useDeprecatedHooks: true } })
  }

  getAllUnbilledAssistances(clientId: number, limit?: number): Observable<Assistance[]> {
    const allowedStatusNames = [
      AssistanceStatusName.PLANNED,
      AssistanceStatusName.HAS_ASSISTANT,
      AssistanceStatusName.SENT_TO_ASSISTANT,
      AssistanceStatusName.CONFIRMED,
      AssistanceStatusName.CANCELED_LATE,
    ]

    return this.statusService.getByNames(allowedStatusNames).pipe(
      switchMap((statuses) => {
        return this.feathers.find(Assistance, {
          queryType: 'narrowToClient',
          $params_clientId: clientId,
          $limit: limit,
          $sort: { plannedFrom: 1 },
          isBilled: false,
          statusId: { $in: statuses.map((s) => s.id) },
        })
      }),
      concatMap((assistances: Assistance[]) => {
        const observables = assistances.map((a) => a.addStatusFromId(this.statusService))
        return forkJoin(observables).pipe(defaultIfEmpty([]))
      }),
      map((assistances: Assistance[]) => assistances.filter((a) => allowedStatusNames.includes(a.status.name))),
    )
  }

  hasAnyUnbilledAssistance(clientId: number): Observable<boolean> {
    return this.getAllUnbilledAssistances(clientId, 1).pipe(
      map((assistances: Assistance[]) => assistances.length > 0),
      first(),
    )
  }

  // TODO task 389
  async createDeletionConfirmationAlertForUser(alertCtrl: AlertController) {
    return new Promise(async (resolve) => {
      const alert = await alertCtrl.create({
        header: 'Opravdu smazat?',
        message: 'Opravdu si přejete smazat klienta?',
        buttons: [
          {
            text: 'Zrušit',
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => {
              resolve(false)
            },
          },
          {
            text: 'Ano, smazat',
            cssClass: 'primary',
            handler: () => {
              resolve(true)
            },
          },
        ],
      })
      alert.present()
    })
  }

  async deactivateWithConfirmation(client: Client, alertCtrl: AlertController, navCtrl: NavController) {
    const unbilledAssistances$ = this.getAllUnbilledAssistances(client.id)
    const hasAnyUnbilledAssistances = await this.hasAnyUnbilledAssistance(client.id).toPromise()

    if (hasAnyUnbilledAssistances) {
      const modal = await this.modalCtrl.create({
        component: SimpleAssistanceListComponent,
        cssClass: 'simpleAssistanceList',
        componentProps: {
          assistances$: unbilledAssistances$,
          headerText: `Nelze deaktivovat - ${client.user.fullName()}`,
          instructionText:
            'Klient nemá uzavřené všechny asistence. V seznamu níže vidíte asistence, které brání deaktivaci klienta. Pokud chcete klienta deaktivovat, tyto asistence buď uzavřete, zrušte anebo smažte.',
        },
      })

      return await modal.present()
    } else {
      const alert = await alertCtrl.create({
        header: 'Opravdu deaktivovat?',
        message:
          'Opravdu si přejete deaktivovat klienta<br><strong>' +
          client.user.firstName +
          ' ' +
          client.user.lastName +
          '</strong>?',
        inputs: [
          {
            name: 'deactivationReason',
            type: 'text',
            placeholder: 'Důvod deaktivace (povinný)',
            label: 'Důvod deaktivace',
            attributes: {
              required: true,
            },
            value: client.deactivationReason,
          },
        ],
        buttons: [
          {
            text: 'Zrušit',
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => {},
          },
          {
            text: 'Deaktivovat',
            cssClass: 'primary',
            handler: (info) => {
              console.log(info)
              if (info.deactivationReason.length > 0) {
                this.deactivate(client, info.deactivationReason)
                // navCtrl.navigateRoot('/menu/app/tabs/clients')
              } else {
                this.deactivateWithConfirmation(client, alertCtrl, navCtrl)
              }
            },
          },
        ],
      })

      await alert.present().then(() => {
        const firstInput: any = document.querySelector('.alert-button.primary')
        firstInput.focus()
        return
      })
    }
  }

  async deactivate(c: Client, reason: string) {
    const clientAllocationId = c.clientAllocations?.[0]?.id
    if (!clientAllocationId) throw new Error(`Client ${c.id} has no client_allocations row`)
    const res = await this.feathers
      .service('client-allocations')
      .patch(clientAllocationId, {
        deactivatedAt: new Date().toISOString(),
        deactivationReason: reason,
      })
    if (c.clientAllocations[0]) {
      c.clientAllocations[0].deactivatedAt = new Date()
      c.clientAllocations[0].deactivationReason = reason
    }
    return res
  }

  async activate(c: Client) {
    const clientAllocationId = c.clientAllocations?.[0]?.id
    if (!clientAllocationId) throw new Error(`Client ${c.id} has no client_allocations row`)
    const res = await this.feathers
      .service('client-allocations')
      .patch(clientAllocationId, {
        deactivatedAt: null,
        deactivationReason: null,
      })
      if (c.clientAllocations[0]) {
        c.clientAllocations[0].deactivatedAt = null
        c.clientAllocations[0].deactivationReason = null
      }
    return res
  }
}
